/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import type { FinderCompetition } from '@sportninja/common/features/profile/finderSettings/types'
import { font, paragraphSmall } from 'src/components/css'
import ScrollContainer from 'src/components/ts-components/ScrollContainer'

interface Props {
  competitions: FinderCompetition[]
}

/** Rows shown before the list scrolls, so a long list can't stretch the card. */
const VISIBLE_ROWS = 4
const ROW_GAP_PX = 4

/**
 * The competitions a finder preference is saved for, each linking to its
 * competition page. Opens in a new tab so the profile stays where it was.
 * Past VISIBLE_ROWS the list scrolls inside the card, on the custom scrollbar.
 */
const FinderPreferenceCompetitions: React.FC<Props> = ({ competitions }) => (
  <ScrollContainer css={scrollStyle}>
    <ul css={listStyle}>
      {competitions.map((competition) => (
        <li key={competition.id}>
          <Link
            to={`${ROUTES.SCHEDULE_ROOT}/${competition.id}`}
            target='_blank'
            //  `noopener` keeps the new tab from reaching back through
            //  `window.opener`; `noreferrer` keeps the profile URL out of it.
            rel='noopener noreferrer'
            css={linkStyle}
          >
            {competition.name}
          </Link>
        </li>
      ))}
    </ul>
  </ScrollContainer>
)

export default FinderPreferenceCompetitions

// ─── Styles ────────────────────────────────────────────────────────────────

/*
 * The rows' own type preset, so `lh` is one row's line height and the cap
 * tracks the preset: VISIBLE_ROWS lines plus the gaps between them.
 */
const scrollStyle = css`
  ${paragraphSmall}
  max-height: calc(${VISIBLE_ROWS}lh + ${(VISIBLE_ROWS - 1) * ROW_GAP_PX}px);
`

const listStyle = css`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${ROW_GAP_PX}px;
`

/* Same link treatment as the event's "Belongs To" competition link. */
const linkStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.PRIMARY};
  overflow-wrap: anywhere;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }
`
